import React, { useState, useMemo } from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import commaNumber from "comma-number";
import dayjs from "dayjs";

const format = commaNumber.bindWith(",", ".");
const fmt = (val) => format(parseFloat(val || 0).toFixed(2));

const MONTH_LABELS = Array.from({ length: 12 }, (_, i) => dayjs().month(i).format("MMM"));

const pivotMatrix = (rows) => {
  const bySupplier = new Map();
  for (const row of rows) {
    if (!bySupplier.has(row.supplier_id)) {
      bySupplier.set(row.supplier_id, {
        supplier_id: row.supplier_id,
        supplier_name: row.supplier_name,
        months: Array(12).fill(0),
        total: 0,
      });
    }
    const entry = bySupplier.get(row.supplier_id);
    const amount = parseFloat(row.amount || 0);
    if (row.month) {
      entry.months[row.month - 1] = amount;
      entry.total += amount;
    }
  }
  return Array.from(bySupplier.values()).sort((a, b) =>
    (a.supplier_name || "").localeCompare(b.supplier_name || "")
  );
};

const SupplierOutflowMatrixTable = ({ printMode = false }) => {
  const { matrix } = useSelector((state) => state.supplierOutflow);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const pivoted = useMemo(() => pivotMatrix(matrix.list), [matrix.list]);

  const monthTotals = useMemo(() => {
    const totals = Array(12).fill(0);
    pivoted.forEach((row) => row.months.forEach((v, i) => (totals[i] += v)));
    return totals;
  }, [pivoted]);

  const grandTotal = useMemo(() => pivoted.reduce((sum, row) => sum + row.total, 0), [pivoted]);

  if (printMode) {
    return (
      <table className="ledger-print-table" style={{ tableLayout: "fixed", width: "100%" }}>
        <thead>
          <tr className="ledger-print-heading">
            <td>No.</td>
            <td>Supplier Name</td>
            {MONTH_LABELS.map((m) => (
              <td key={m} style={{ textAlign: "right" }}>{m}</td>
            ))}
            <td style={{ textAlign: "right" }}>Total</td>
          </tr>
        </thead>
        <tbody>
          {pivoted.map((row, index) => (
            <tr key={row.supplier_id}>
              <td>{index + 1}</td>
              <td>{row.supplier_name}</td>
              {row.months.map((v, i) => (
                <td key={i} style={{ textAlign: "right" }}>{fmt(v)}</td>
              ))}
              <td style={{ textAlign: "right" }}>{fmt(row.total)}</td>
            </tr>
          ))}
          <tr className="ledger-total-row">
            <td colSpan={2} style={{ textAlign: "right" }}>Grand Total</td>
            {monthTotals.map((v, i) => (
              <td key={i} style={{ textAlign: "right" }}>{fmt(v)}</td>
            ))}
            <td style={{ textAlign: "right" }}>{fmt(grandTotal)}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 50,
    },
    {
      title: "Supplier Name",
      dataIndex: "supplier_name",
      fixed: "left",
      width: 200,
    },
    ...MONTH_LABELS.map((label, i) => ({
      title: label,
      align: "right",
      width: 100,
      render: (_, row) => fmt(row.months[i]),
    })),
    {
      title: "Total",
      align: "right",
      width: 130,
      fixed: "right",
      render: (_, row) => <strong>{fmt(row.total)}</strong>,
    },
  ];

  return (
    <Table
      rowKey="supplier_id"
      columns={columns}
      dataSource={pivoted}
      loading={matrix.loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={setPagination}
      bordered
      scroll={{ x: "max-content" }}
      size="middle"
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={2}>
            <strong>Grand Total</strong>
          </Table.Summary.Cell>
          {monthTotals.map((v, i) => (
            <Table.Summary.Cell index={i + 2} align="right" key={i}>
              <strong>{fmt(v)}</strong>
            </Table.Summary.Cell>
          ))}
          <Table.Summary.Cell index={14} align="right">
            <strong>{fmt(grandTotal)}</strong>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
};

export default SupplierOutflowMatrixTable;
